<template>
    <base-material-card icon="mdi-database-check" title="Data Status">
        <v-tabs v-model="selectedTab">
            <v-tab
                class="mr-3"
                v-for="tab in mwsConfigurationOptions"
                :key="tab.value"
                @click="onClickTab(tab.value)"
            >
                <v-icon class="mr-2">mdi-amazon</v-icon>
                {{ tab.text }}
            </v-tab>
        </v-tabs>


        <!-- Historical Data Section -->
        <v-card class="mt-4" outlined>
            <v-card-title>
                <v-icon class="mr-2">mdi-history</v-icon>
                Historical Data Status
                <v-spacer></v-spacer>
                <v-chip v-if="availableFrom" small class="mr-2">
                    Available from {{ formatDate(availableFrom) }}
                </v-chip>
            </v-card-title>
            <v-card-text>
                <!-- Date Range Controls -->
                <v-row class="mb-4">
                    <v-col cols="12" md="3">
                        <v-text-field
                            v-model="customStartDate"
                            label="Start Date"
                            type="date"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-text-field
                            v-model="customEndDate"
                            label="End Date"
                            type="date"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn
                            @click="loadCustomDateRange"
                            color="primary"
                            :loading="loading"
                            block
                        >
                            Load Range
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn @click="resetToRecent" outlined block>
                            Recent Data
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn
                            @click="loadAllHistory"
                            outlined
                            color="info"
                            block
                            :loading="loading"
                        >
                            Load All
                        </v-btn>
                    </v-col>
                </v-row>

                <!-- Timeline View -->
                <div v-if="timelineData.length">
                    <v-timeline align-top dense>
                        <v-timeline-item
                            v-for="day in timelineData"
                            :key="day.date"
                            :color="
                                selectedMwsConfigId == 0
                                    ? (getOverallDayScoreForAllApps(day) >= 0.7
                                        ? 'green'
                                        : getOverallDayScoreForAllApps(day) >= 0.5
                                        ? 'orange'
                                        : 'red')
                                    : (getOverallDayScore(day) >= 0.7
                                        ? 'green'
                                        : getOverallDayScore(day) >= 0.5
                                        ? 'orange'
                                        : 'red')
                            "
                            small
                        >
                            <template v-slot:opposite>
                                <span class="headline font-weight-bold">
                                    {{ formatDate(day.date) }}
                                </span>
                            </template>

                            <!-- Single App View (Individual tab selected) -->
                            <v-card v-if="selectedMwsConfigId != 0" class="elevation-2">
                                <v-card-title class="text-h6">
                                    {{ day.date }}
                                    <v-spacer></v-spacer>
                                    <v-chip
                                        :color="
                                            getOverallDayScore(day) >= 0.7
                                                ? 'green'
                                                : getOverallDayScore(day) >= 0.5
                                                ? 'orange'
                                                : 'red'
                                        "
                                        small
                                        dark
                                    >
                                        {{
                                            Math.round(
                                                getOverallDayScore(day) * 100
                                            )
                                        }}%
                                    </v-chip>
                                </v-card-title>
                                <v-card-text>
                                    <v-row dense>
                                        <v-col
                                            cols="6"
                                            sm="4"
                                            md="2"
                                            v-for="(
                                                check, key
                                            ) in dailyChecklistItems"
                                            :key="key"
                                        >
                                            <div
                                                class="text-center checklist-item"
                                                @click="openDetailModal(day, key, check)"
                                            >
                                                <v-icon
                                                    :color="
                                                        getScoreColor(
                                                            day[key].score,
                                                            day[key].max_score
                                                        )
                                                    "
                                                    small
                                                >
                                                    {{ check.icon }}
                                                </v-icon>
                                                <div class="caption">
                                                    {{ day[key].score }}/{{
                                                        day[key].max_score
                                                    }}
                                                </div>
                                                <v-tooltip bottom>
                                                    <template
                                                        v-slot:activator="{
                                                            on,
                                                            attrs,
                                                        }"
                                                    >
                                                        <small
                                                            class="
                                                                text--secondary
                                                                d-block
                                                            "
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        >
                                                            {{
                                                                day[key]
                                                                    .last_update
                                                            }}
                                                        </small>
                                                    </template>
                                                    <span
                                                        >{{ check.title }} -
                                                        Last Update</span
                                                    >
                                                </v-tooltip>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- All Apps View (All tab selected) -->
                            <div v-else>
                                <v-card class="elevation-2 mb-2">
                                    <v-card-title class="text-h6">
                                        {{ day.date }}
                                        <v-spacer></v-spacer>
                                        <v-chip
                                            :color="
                                                getOverallDayScoreForAllApps(day) >= 0.7
                                                    ? 'green'
                                                    : getOverallDayScoreForAllApps(day) >= 0.5
                                                    ? 'orange'
                                                    : 'red'
                                            "
                                            small
                                            dark
                                        >
                                            {{
                                                Math.round(
                                                    getOverallDayScoreForAllApps(day) * 100
                                                )
                                            }}%
                                        </v-chip>
                                    </v-card-title>
                                </v-card>

                                <!-- App Cards -->
                                <v-card
                                    v-for="app in day.apps"
                                    :key="`${day.date}-${app.id}`"
                                    class="elevation-1 mb-3 ml-4"
                                    outlined
                                >
                                    <v-card-subtitle class="pb-0">
                                        <v-icon small class="mr-1">mdi-amazon</v-icon>
                                        <strong>{{ app.name }}</strong>
                                    </v-card-subtitle>
                                    <v-card-text>
                                        <v-row dense>
                                            <v-col
                                                cols="6"
                                                sm="4"
                                                md="2"
                                                v-for="(
                                                    check, key
                                                ) in dailyChecklistItems"
                                                :key="key"
                                            >
                                                <div
                                                    class="text-center checklist-item"
                                                    @click="openDetailModalForApp(day, app, key, check)"
                                                >
                                                    <v-icon
                                                        :color="
                                                            getScoreColor(
                                                                app.data[key].score,
                                                                app.data[key].max_score
                                                            )
                                                        "
                                                        small
                                                    >
                                                        {{ check.icon }}
                                                    </v-icon>
                                                    <div class="caption">
                                                        {{ app.data[key].score }}/{{
                                                            app.data[key].max_score
                                                        }}
                                                    </div>
                                                    <v-tooltip bottom>
                                                        <template
                                                            v-slot:activator="{
                                                                on,
                                                                attrs,
                                                            }"
                                                        >
                                                            <small
                                                                class="
                                                                    text--secondary
                                                                    d-block
                                                                "
                                                                v-bind="attrs"
                                                                v-on="on"
                                                            >
                                                                {{
                                                                    app.data[key]
                                                                        .last_update
                                                                }}
                                                            </small>
                                                        </template>
                                                        <span
                                                            >{{ check.title }} -
                                                            Last Update</span
                                                        >
                                                    </v-tooltip>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </div>
                        </v-timeline-item>
                    </v-timeline>

                    <!-- Pagination Controls -->
                    <v-card
                        v-if="!isCustomRange"
                        class="mt-4 text-center"
                        outlined
                    >
                        <v-card-text>
                            <v-btn
                                @click="loadMoreHistory"
                                :loading="loading"
                                :disabled="!hasMore"
                                color="primary"
                                outlined
                            >
                                <v-icon left>mdi-history</v-icon>
                                Load More History
                                <span v-if="!hasMore" class="ml-2"
                                    >(No more data)</span
                                >
                            </v-btn>
                            <div class="mt-2 text--secondary">
                                <small v-if="pagination">
                                    Page {{ pagination.current_page }} •
                                    {{ timelineData.length }} days loaded
                                    <span v-if="pagination.available_from">
                                        • Available from
                                        {{
                                            formatDate(
                                                pagination.available_from
                                            )
                                        }}
                                    </span>
                                </small>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>

                <!-- Loading State -->
                <div v-else-if="loading" class="text-center py-8">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        size="60"
                    ></v-progress-circular>
                    <div class="mt-4 text--secondary">
                        Loading historical data...
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-8">
                    <v-icon size="60" color="grey lighten-2"
                        >mdi-calendar-blank</v-icon
                    >
                    <div class="mt-4 text--secondary">
                        No data available for selected configuration.
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Detail Modal -->
        <v-dialog v-model="detailModal" max-width="800px" scrollable>
            <v-card>
                <v-card-title class="text-h5">
                    <v-icon :color="selectedDetailData.iconColor" class="mr-2">
                        {{ selectedDetailData.icon }}
                    </v-icon>
                    {{ selectedDetailData.title }} Details
                    <span v-if="selectedMwsConfigId == 0 && !selectedDetailData.appName" class="ml-2 text--secondary">(All Apps)</span>
                    <span v-if="selectedDetailData.appName" class="ml-2 text--secondary">({{ selectedDetailData.appName }})</span>
                    <v-spacer></v-spacer>
                    <v-chip
                        :color="selectedDetailData.chipColor"
                        small
                        dark
                    >
                        {{ selectedDetailData.score }}/{{ selectedDetailData.maxScore }}
                    </v-chip>
                </v-card-title>
                <v-card-subtitle>
                    {{ selectedDetailData.date }} • Last updated: {{ selectedDetailData.lastUpdate }}
                </v-card-subtitle>

                <v-divider></v-divider>

                <v-card-text class="pa-0">
                    <v-container>
                        <!-- Loading state -->
                        <div v-if="detailLoading" class="text-center py-8">
                            <v-progress-circular
                                indeterminate
                                color="primary"
                                size="40"
                            ></v-progress-circular>
                            <div class="mt-4 text--secondary">
                                Loading detailed information...
                            </div>
                        </div>

                        <!-- Detail content -->
                        <div v-else>
                            <!-- Summary Section -->
                            <v-row class="mb-4">
                                <v-col cols="12">
                                    <v-card outlined>
                                        <v-card-title class="text-h6">
                                            <v-icon class="mr-2">mdi-chart-line</v-icon>
                                            Summary
                                        </v-card-title>
                                        <v-card-text>
                                            <v-row>
                                                <v-col :cols="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 6 : 6" :md="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 2 : 3">
                                                    <div class="text-center">
                                                        <div class="text-h4" :class="getScoreTextColor(selectedDetailData.score, selectedDetailData.maxScore)">
                                                            {{ selectedDetailData.score }}
                                                        </div>
                                                        <div class="text--secondary">Records Found</div>
                                                    </div>
                                                </v-col>
                                                <v-col :cols="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 6 : 6" :md="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 2 : 3">
                                                    <div class="text-center">
                                                        <div class="text-h4">
                                                            {{ selectedDetailData.maxScore }}
                                                        </div>
                                                        <div class="text--secondary">Expected</div>
                                                    </div>
                                                </v-col>
                                                <v-col cols="6" md="2" v-if="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count">
                                                    <div class="text-center">
                                                        <div class="text-h4 blue--text">
                                                            {{ detailData.detail.additional_data.apps_count }}
                                                        </div>
                                                        <div class="text--secondary">Apps</div>
                                                    </div>
                                                </v-col>
                                                <v-col :cols="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 6 : 6" :md="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 3 : 3">
                                                    <div class="text-center">
                                                        <div class="text-h4" :class="getPercentageTextColor(selectedDetailData.score, selectedDetailData.maxScore)">
                                                            {{ getPercentage(selectedDetailData.score, selectedDetailData.maxScore) }}%
                                                        </div>
                                                        <div class="text--secondary">Completion</div>
                                                    </div>
                                                </v-col>
                                                <v-col :cols="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 6 : 6" :md="selectedMwsConfigId == 0 && detailData && detailData.detail && detailData.detail.additional_data && detailData.detail.additional_data.apps_count ? 3 : 3">
                                                    <div class="text-center">
                                                        <v-icon
                                                            size="40"
                                                            :color="getScoreColor(selectedDetailData.score, selectedDetailData.maxScore)"
                                                        >
                                                            {{ getStatusIcon(selectedDetailData.score, selectedDetailData.maxScore) }}
                                                        </v-icon>
                                                        <div class="text--secondary">Status</div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Detailed Information -->
                            <v-row v-if="detailData">
                                <v-col cols="12">
                                    <v-card outlined>
                                        <v-card-title class="text-h6">
                                            <v-icon class="mr-2">mdi-information</v-icon>
                                            Detailed Information
                                        </v-card-title>
                                        <v-card-text>
                                            <!-- Data Status Log Details -->
                                            <div v-if="detailData.detail">
                                                <!-- Status Metrics -->
                                                <v-row class="mb-4">
                                                    <v-col cols="12" md="6">
                                                        <v-card outlined class="pa-3">
                                                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                                                <v-icon small class="mr-1">mdi-chart-box</v-icon>
                                                                Status Metrics
                                                            </div>
                                                            <v-row dense>
                                                                <v-col cols="6">
                                                                    <div class="text-caption text--secondary">Total Count</div>
                                                                    <div class="text-h6">{{ detailData.detail.total_count || 'N/A' }}</div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div class="text-caption text--secondary">Last Update</div>
                                                                    <div class="text-body-2">{{ formatDateTime(detailData.detail.last_update) }}</div>
                                                                </v-col>
                                                            </v-row>
                                                        </v-card>
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-card outlined class="pa-3">
                                                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                                                <v-icon small class="mr-1">mdi-clock</v-icon>
                                                                Record Information
                                                            </div>
                                                            <v-row dense>
                                                                <v-col cols="6">
                                                                    <div class="text-caption text--secondary">Check Date</div>
                                                                    <div class="text-body-2">{{ detailData.detail.check_date }}</div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div class="text-caption text--secondary">Updated At</div>
                                                                    <div class="text-body-2">{{ formatDateTime(detailData.detail.updated_at) }}</div>
                                                                </v-col>
                                                            </v-row>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>

                                                <!-- Criteria -->
                                                <v-row class="mb-4" v-if="detailData.detail.criteria && Object.keys(detailData.detail.criteria).length">
                                                    <v-col cols="12">
                                                        <v-card outlined class="pa-3">
                                                            <div class="text-subtitle-1 font-weight-bold mb-3">
                                                                <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>
                                                                Validation Criteria
                                                            </div>
                                                            <v-row dense>
                                                                <v-col
                                                                    cols="12"
                                                                    md="6"
                                                                    v-for="(value, key) in detailData.detail.criteria"
                                                                    :key="key"
                                                                    class="mb-2"
                                                                >
                                                                    <div class="d-flex align-center">
                                                                        <v-icon
                                                                            small
                                                                            :color="value ? 'green' : 'red'"
                                                                            class="mr-2"
                                                                        >
                                                                            {{ value ? 'mdi-check' : 'mdi-close' }}
                                                                        </v-icon>
                                                                        <div>
                                                                            <div class="text-body-2">{{ formatCriteriaKey(key) }}</div>
                                                                            <div class="text-caption text--secondary">
                                                                                {{ value ? 'Passed' : 'Failed' }}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-col>
                                                            </v-row>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>

                                                <!-- Additional Data -->
                                                <v-row v-if="detailData.detail.additional_data && Object.keys(detailData.detail.additional_data).length">
                                                    <v-col cols="12">
                                                        <v-card outlined class="pa-3">
                                                            <div class="text-subtitle-1 font-weight-bold mb-3">
                                                                <v-icon small class="mr-1">mdi-information</v-icon>
                                                                Additional Information
                                                            </div>
                                                            <v-row dense>
                                                                <v-col
                                                                    cols="12"
                                                                    md="6"
                                                                    v-for="(value, key) in detailData.detail.additional_data"
                                                                    :key="key"
                                                                    class="mb-2"
                                                                >
                                                                    <div class="text-caption text--secondary">{{ formatCriteriaKey(key) }}</div>
                                                                    <div class="text-body-2">{{ formatValue(value) }}</div>
                                                                </v-col>
                                                            </v-row>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                            <div v-else-if="detailData.message" class="text-center py-4">
                                                <v-icon size="60" color="grey lighten-2">mdi-information-outline</v-icon>
                                                <div class="mt-2 text--secondary">{{ detailData.message }}</div>
                                            </div>
                                            <div v-else class="text-center py-4">
                                                <v-icon size="60" color="grey lighten-2">mdi-database-off</v-icon>
                                                <div class="mt-2 text--secondary">No detailed data available</div>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Error Logs (if any) -->
                            <v-row v-if="detailData && detailData.errors && detailData.errors.length">
                                <v-col cols="12">
                                    <v-card outlined color="error" dark>
                                        <v-card-title class="text-h6">
                                            <v-icon class="mr-2">mdi-alert</v-icon>
                                            Recent Errors
                                        </v-card-title>
                                        <v-card-text>
                                            <v-timeline dense>
                                                <v-timeline-item
                                                    v-for="(error, index) in detailData.errors"
                                                    :key="index"
                                                    color="white"
                                                    small
                                                >
                                                    <v-card class="elevation-2">
                                                        <v-card-text>
                                                            <div class="font-weight-bold">{{ error.message }}</div>
                                                            <div class="text--secondary">{{ formatDateTime(error.created_at) }}</div>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-timeline-item>
                                            </v-timeline>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </div>
                    </v-container>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="detailModal = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </base-material-card>
</template>

<script>
import { tz, sourceTz } from "@/constants/Time";

export default {
    computed: {
        mwsConfigurationOptions() {
            return this.$store.getters.mwsConfigurationOptions;
        },
        dailyChecklistItems() {
            return {
                financial_transaction: {
                    title: "Financial Transaction",
                    icon: "mdi-currency-usd",
                },
                order: {
                    title: "Order",
                    icon: "mdi-cart",
                },
                product_listing: {
                    title: "Product Listing",
                    icon: "mdi-package-variant",
                },
                amazon_ad: {
                    title: "Amazon Ad",
                    icon: "mdi-advertisements",
                },
                inventory_report: {
                    title: "Inventory Report",
                    icon: "mdi-warehouse",
                },
            };
        },
    },
    watch: {
        selectedMwsConfigId: {
            immediate: true,
            handler(newVal, oldVal) {
                if ((newVal === null || newVal === undefined) || newVal === oldVal) {
                    return;
                }
                this.loadTimelineData();
            },
        },
    },
    data() {
        return {
            tz: tz,
            sourceTz: sourceTz,
            selectedTab: 0,
            selectedMwsConfigId: null,
            timelineData: [],
            loading: false,
            pagination: null,
            hasMore: true,
            currentPage: 1,
            customStartDate: "",
            customEndDate: "",
            isCustomRange: false,
            availableFrom: null,
            detailModal: false,
            detailLoading: false,
            detailData: null,
            selectedDetailData: {
                title: "",
                icon: "",
                iconColor: "",
                chipColor: "",
                score: 0,
                maxScore: 0,
                date: "",
                lastUpdate: "",
                type: "",
            },
        };
    },
    methods: {
        onClickTab(id) {
            this.selectedMwsConfigId = id;
        },
        findSelectedTab(tabs) {
            if (!tabs || !tabs.length) {
                return;
            }
            this.selectedTab = 0;
            this.selectedMwsConfigId = tabs[0].value;
        },
        async loadTimelineData() {
            if (this.selectedMwsConfigId === null || this.selectedMwsConfigId === undefined) {
                return;
            }

            this.loading = true;
            this.currentPage = 1;
            this.isCustomRange = false;

            try {
                const response = await this.$admin.http.get(
                    "/api/data-status/historical",
                    {
                        params: {
                            mws_configuration_id: this.selectedMwsConfigId,
                            page: 1,
                            per_page: 30,
                        },
                    }
                );

                this.timelineData = response.data.data.reverse(); // Most recent first
                this.pagination = response.data.pagination;
                this.hasMore = response.data.pagination.has_more;
                this.availableFrom = response.data.pagination.available_from;
            } catch (error) {
                console.error("Failed to load timeline data:", error);
                this.timelineData = [];
            } finally {
                this.loading = false;
            }
        },

        async loadMoreHistory() {
            if (!this.hasMore || this.loading)
                return;

            this.loading = true;
            this.currentPage += 1;

            try {
                const response = await this.$admin.http.get(
                    "/api/data-status/historical",
                    {
                        params: {
                            mws_configuration_id: this.selectedMwsConfigId,
                            page: this.currentPage,
                            per_page: 30,
                        },
                    }
                );

                // Append new data (older dates)
                const newData = response.data.data.reverse();
                this.timelineData = [...this.timelineData, ...newData];
                this.pagination = response.data.pagination;
                this.hasMore = response.data.pagination.has_more;
            } catch (error) {
                console.error("Failed to load more history:", error);
                this.currentPage -= 1; // Revert page increment
            } finally {
                this.loading = false;
            }
        },

        async loadCustomDateRange() {
            if (!this.customStartDate ||
                !this.customEndDate
            ) {
                return;
            }

            this.loading = true;
            this.isCustomRange = true;

            try {
                const response = await this.$admin.http.get(
                    "/api/data-status/historical",
                    {
                        params: {
                            mws_configuration_id: this.selectedMwsConfigId,
                            start_date: this.customStartDate,
                            end_date: this.customEndDate,
                            page: 1,
                            per_page: 365, // Allow up to 1 year in custom range
                        },
                    }
                );

                this.timelineData = response.data.data.reverse(); // Most recent first
                this.pagination = response.data.pagination;
                this.hasMore = false; // Custom ranges don't have "load more"
            } catch (error) {
                console.error("Failed to load custom date range:", error);
                this.timelineData = [];
            } finally {
                this.loading = false;
            }
        },

        async loadAllHistory() {
            if (!this.selectedMwsConfigId || this.loading) return;

            if (
                !confirm(
                    "This will load all historical data, which may take some time. Continue?"
                )
            ) {
                return;
            }

            this.loading = true;
            this.isCustomRange = true;

            try {
                const response = await this.$admin.http.get(
                    "/api/data-status/historical",
                    {
                        params: {
                            mws_configuration_id: this.selectedMwsConfigId,
                            start_date: this.availableFrom,
                            end_date: new Date().toISOString().split("T")[0],
                            page: 1,
                            per_page: 365, // Load in chunks
                        },
                    }
                );

                this.timelineData = response.data.data.reverse();
                this.pagination = response.data.pagination;
                this.hasMore = false;
            } catch (error) {
                console.error("Failed to load all history:", error);
                this.timelineData = [];
            } finally {
                this.loading = false;
            }
        },

        resetToRecent() {
            this.customStartDate = "";
            this.customEndDate = "";
            this.isCustomRange = false;
            this.loadTimelineData();
        },
        getScoreColor(score, maxScore) {
            const percentage = score / maxScore;
            if (percentage >= 0.8) return "green";
            if (percentage >= 0.6) return "orange";
            return "red";
        },
        getOverallDayScore(day) {
            const checks = Object.values(day).filter(
                (item) => item.score !== undefined
            );
            if (checks.length === 0) return 0;

            const totalScore = checks.reduce(
                (sum, check) => sum + check.score,
                0
            );
            const totalMaxScore = checks.reduce(
                (sum, check) => sum + check.max_score,
                0
            );

            return totalMaxScore > 0 ? totalScore / totalMaxScore : 0;
        },
        formatDate(dateStr) {
            return new Date(dateStr).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
        },
        formatDateTime(dateStr) {
            if (!dateStr) return "N/A";
            return new Date(dateStr).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        async openDetailModal(day, key, check) {
            this.selectedDetailData = {
                title: check.title,
                icon: check.icon,
                iconColor: this.getScoreColor(day[key].score, day[key].max_score),
                chipColor: this.getScoreColor(day[key].score, day[key].max_score),
                score: day[key].score,
                maxScore: day[key].max_score,
                date: day.date,
                lastUpdate: day[key].last_update,
                type: key,
            };

            this.detailModal = true;
            this.detailLoading = true;
            this.detailData = null;

            try {
                const response = await this.$admin.http.get("/api/data-status/detail", {
                    params: {
                        mws_configuration_id: this.selectedMwsConfigId,
                        date: day.date,
                        type: key,
                    },
                });

                this.detailData = response.data;
            } catch (error) {
                console.error("Failed to load detail data:", error);
                this.detailData = {
                    message: "Failed to load detailed information. Please try again.",
                };
            } finally {
                this.detailLoading = false;
            }
        },
        getTableHeaders(type) {
            const commonHeaders = [
                { text: "ID", value: "id", sortable: true },
                { text: "Created At", value: "created_at", sortable: true },
                { text: "Updated At", value: "updated_at", sortable: true },
            ];

            switch (type) {
                case "financial_transaction":
                    return [
                        { text: "Financial Event ID", value: "financial_event_id", sortable: true },
                        { text: "Amount", value: "amount", sortable: true },
                        { text: "Transaction Type", value: "transaction_type", sortable: true },
                        { text: "Entity Type", value: "entity_type", sortable: true },
                        { text: "Order ID", value: "AmazonOrderId", sortable: true },
                        { text: "SKU", value: "SellerSKU", sortable: true },
                        ...commonHeaders,
                    ];
                case "order":
                    return [
                        { text: "Amazon Order ID", value: "AmazonOrderId", sortable: true },
                        { text: "Status", value: "OrderStatus", sortable: true },
                        { text: "Items Shipped", value: "NumberOfItemsShipped", sortable: true },
                        { text: "Total Amount", value: "OrderTotal_Amount", sortable: true },
                        { text: "Currency", value: "OrderTotal_CurrencyCode", sortable: true },
                        { text: "Purchase Date", value: "PurchaseDate", sortable: true },
                        ...commonHeaders,
                    ];
                case "product_listing":
                    return [
                        { text: "SKU", value: "SellerSKU", sortable: true },
                        { text: "ASIN", value: "ASIN", sortable: true },
                        { text: "Title", value: "AttributeSets_Title", sortable: false },
                        { text: "Marketplace ID", value: "MarketplaceId", sortable: true },
                        ...commonHeaders,
                    ];
                case "amazon_ad":
                    return [
                        { text: "Campaign ID", value: "campaign_id", sortable: true },
                        { text: "Campaign Name", value: "campaign_name", sortable: false },
                        { text: "Impressions", value: "impressions", sortable: true },
                        { text: "Clicks", value: "clicks", sortable: true },
                        { text: "Spend", value: "spend", sortable: true },
                        { text: "Date", value: "date", sortable: true },
                        ...commonHeaders,
                    ];
                case "inventory_report":
                    return [
                        { text: "SKU", value: "seller_sku", sortable: true },
                        { text: "FNSKU", value: "fnsku", sortable: true },
                        { text: "ASIN", value: "asin", sortable: true },
                        { text: "Condition", value: "condition", sortable: true },
                        { text: "Total Quantity", value: "total_quantity", sortable: true },
                        { text: "Available Quantity", value: "available_quantity", sortable: true },
                        ...commonHeaders,
                    ];
                default:
                    return commonHeaders;
            }
        },
        getScoreTextColor(score, maxScore) {
            const percentage = score / maxScore;
            if (percentage >= 0.8) return "green--text text--darken-2";
            if (percentage >= 0.6) return "orange--text text--darken-2";
            return "red--text text--darken-2";
        },
        getPercentageTextColor(score, maxScore) {
            return this.getScoreTextColor(score, maxScore);
        },
        getPercentage(score, maxScore) {
            return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
        },
        getStatusIcon(score, maxScore) {
            const percentage = score / maxScore;
            if (percentage >= 0.8) return "mdi-check-circle";
            if (percentage >= 0.6) return "mdi-alert-circle";
            return "mdi-close-circle";
        },
        formatCriteriaKey(key) {
            return key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
        },
        formatValue(value) {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        },
        getOverallDayScoreForAllApps(day) {
            if (!day || !day.apps || day.apps.length === 0) {
                return 0;
            }

            let totalScore = 0;
            let totalMaxScore = 0;

            day.apps.forEach(app => {
                if (app && app.data) {
                    Object.values(app.data).forEach(dataType => {
                        if (dataType && dataType.score !== undefined && dataType.max_score !== undefined) {
                            totalScore += dataType.score;
                            totalMaxScore += dataType.max_score;
                        }
                    });
                }
            });

            return totalMaxScore > 0 ? totalScore / totalMaxScore : 0;
        },
        async openDetailModalForApp(day, app, key, check) {
            this.selectedDetailData = {
                title: check.title,
                icon: check.icon,
                iconColor: this.getScoreColor(app.data[key].score, app.data[key].max_score),
                chipColor: this.getScoreColor(app.data[key].score, app.data[key].max_score),
                score: app.data[key].score,
                maxScore: app.data[key].max_score,
                date: day.date,
                lastUpdate: app.data[key].last_update,
                type: key,
                appName: app.name,
            };

            this.detailModal = true;
            this.detailLoading = true;
            this.detailData = null;

            try {
                const response = await this.$admin.http.get("/api/data-status/detail", {
                    params: {
                        mws_configuration_id: app.id, // Use the specific app's ID directly
                        date: day.date,
                        type: key,
                    },
                });

                this.detailData = response.data;
            } catch (error) {
                console.error("Failed to load detail data:", error);
                this.detailData = {
                    message: "Failed to load detailed information. Please try again.",
                };
            } finally {
                this.detailLoading = false;
            }
        },
    },
    mounted() {
        this.$watch(
            () => this.mwsConfigurationOptions,
            (newVal) => {
                if (newVal && newVal.length) {
                    this.findSelectedTab(newVal);
                }
            },
            { immediate: true }
        );
    },
};
</script>

<style lang="scss" scoped>
.v-timeline-item__body {
    padding-bottom: 20px;
}

.v-card {
    transition: all 0.3s ease;
}

.v-card:hover {
    elevation: 4;
}

.checklist-item {
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
        transform: translateY(-1px);
    }
}

.theme--dark .checklist-item:hover {
    background-color: rgba(255, 255, 255, 0.08);
}
</style>
